import type { DashboardsWidgetConfig } from '@smarthome/api';
import { Save, Trash2 } from 'lucide-react';
import { useState, useCallback } from 'react';

import { getWidgetDefinition } from '../registry';
import type { EditorWidget } from '../types';

import { Button, SlideOutPanel, TextField } from '@/components/ui';

export interface EditWidgetModalProps {
  widget: EditorWidget | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (id: string, title: string, config: DashboardsWidgetConfig) => void;
  onDelete: (id: string) => void;
}

/**
 * Merge widget config with defaults, filtering out undefined values
 */
function mergeConfig(
  defaultConfig: DashboardsWidgetConfig,
  widgetConfig: DashboardsWidgetConfig
): DashboardsWidgetConfig {
  const filteredConfig = Object.fromEntries(
    Object.entries(widgetConfig).filter(([, value]) => value !== undefined)
  );
  return { ...defaultConfig, ...filteredConfig };
}

export function EditWidgetModal({
  widget,
  isOpen,
  onClose,
  onSave,
  onDelete,
}: EditWidgetModalProps) {
  const widgetDef = widget ? getWidgetDefinition(widget.type) : null;

  // Initialize state from widget - component remounts for each widget via key prop
  const initialConfig =
    widget && widgetDef ? mergeConfig(widgetDef.defaultConfig, widget.config) : {};

  const [title, setTitle] = useState(widget?.title ?? '');
  const [config, setConfig] = useState<DashboardsWidgetConfig>(initialConfig);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleClose = useCallback(() => {
    setErrors({});
    setShowDeleteConfirm(false);
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(() => {
    if (!widget || !widgetDef) return;

    // Validate config
    const result = widgetDef.configSchema.safeParse(config);
    if (!result.success) {
      const newErrors: Record<string, string> = {};
      for (const issue of result.error.issues) {
        const path = issue.path.join('.');
        newErrors[path] = issue.message;
      }
      setErrors(newErrors);
      return;
    }

    // Validate title
    if (!title.trim()) {
      setErrors({ title: 'Title is required' });
      return;
    }

    onSave(widget.id, title.trim(), config);
    handleClose();
  }, [widget, widgetDef, config, title, onSave, handleClose]);

  const handleDelete = useCallback(() => {
    if (!widget) return;
    onDelete(widget.id);
    handleClose();
  }, [widget, onDelete, handleClose]);

  if (!widget || !widgetDef) return null;

  const ConfigForm = widgetDef.ConfigForm;
  const Icon = widgetDef.icon;

  return (
    <SlideOutPanel
      isOpen={isOpen}
      onClose={handleClose}
      title={showDeleteConfirm ? 'Delete Widget' : 'Edit Widget'}
      subtitle={
        showDeleteConfirm
          ? `Are you sure you want to delete "${widget.title}"?`
          : widgetDef.displayName
      }
      size="lg"
      footer={
        showDeleteConfirm ? (
          <>
            <Button variant="ghost" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="ghost"
              onClick={() => setShowDeleteConfirm(true)}
              className="mr-auto text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
            <Button variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
            <Button onClick={handleSubmit}>
              <Save className="mr-2 h-4 w-4" />
              Save
            </Button>
          </>
        )
      }
    >
      {showDeleteConfirm ? (
        <div className="flex items-center gap-4 rounded-lg bg-red-50 p-4 dark:bg-red-500/10">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-red-100 dark:bg-red-500/20">
            <Icon className="h-5 w-5 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <p className="text-sm font-medium text-red-800 dark:text-red-300">{widget.title}</p>
            <p className="mt-0.5 text-xs text-red-600 dark:text-red-400">
              This action cannot be undone.
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <TextField
            label="Widget Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            error={errors['title']}
            placeholder={widgetDef.displayName}
          />

          <div className="border-t border-gray-100 pt-4 dark:border-white/[0.04]">
            <ConfigForm
              config={config as typeof widgetDef.defaultConfig}
              onChange={setConfig}
              errors={errors}
            />
          </div>
        </div>
      )}
    </SlideOutPanel>
  );
}
